// src/frontend/src/pages/PrivacyPolicy.tsx
import React from 'react';
import HomeLayout from '../components/Layout/HomeLayout';

const PrivacyPolicy: React.FC = () => {
  return (
    <HomeLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Information You Provide</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Account information (name, email address, organization)</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Simulation conversations and responses</li>
                <li>Custom scenario content you create</li>
                <li>Feedback and support communications</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Information Collected Automatically</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Usage data (sessions completed, time spent, feature usage)</li>
                <li>Device information (browser type, operating system)</li>
                <li>IP address and general location information</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Google Authentication</h3>
              <p className="text-gray-700 mb-4">
                When you sign in with Google, we receive your basic profile information (name, email, profile picture) 
                as permitted by your Google account settings. We do not access your other Google services or data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Provide and improve PM Coach services</li>
                <li>Generate AI-powered coaching feedback</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send service notifications and account updates</li>
                <li>Provide customer support</li>
                <li>Analyze usage patterns to improve our platform</li>
                <li>Ensure platform security and prevent fraud</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing</h2>
              
              <p className="text-gray-700 mb-4">
                We do not sell your personal information. We may share information in these limited circumstances:
              </p>
              
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li><strong>Service Providers:</strong> Stripe (payments), Claude AI (conversation processing), email services</li>
                <li><strong>Team Features:</strong> Your coaching progress with your team administrator (if applicable)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Encryption in transit and at rest</li>
                <li>Secure data centers and cloud infrastructure</li>
                <li>Regular security audits and updates</li>
                <li>Limited access to personal data by employees</li>
                <li>Secure payment processing through Stripe</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Your Rights and Choices</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Access and Control</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Access your personal information through your account settings</li>
                <li>Update or correct your profile information</li>
                <li>Export your coaching session data</li>
                <li>Delete your account and associated data</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Email Preferences</h3>
              <p className="text-gray-700 mb-4">
                You can control email notifications in your account settings. You cannot opt out of essential 
                service communications (e.g., billing notifications, security alerts).
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Cookies</h3>
              <p className="text-gray-700 mb-4">
                Most browsers allow you to control cookies through their settings. Disabling cookies may 
                limit some platform functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as your account is active or as needed to provide services:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>Account data: Until account deletion</li>
                <li>Coaching sessions: Until account deletion or upon request</li>
                <li>Payment records: As required by law (typically 7 years)</li>
                <li>Usage analytics: Aggregated data may be retained indefinitely</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                PM Coach is intended for professional use by adults. We do not knowingly collect information 
                from children under 16. If we become aware of such collection, we will delete the information promptly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Users</h2>
              <p className="text-gray-700 mb-4">
                PM Coach is operated from the United States. If you are located outside the US, your information 
                will be transferred to and processed in the US, which may have different privacy laws than your country.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy periodically. We will notify you of significant changes via 
                email or through the platform. Your continued use constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                For questions about this Privacy Policy or our privacy practices, contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> privacy@pmcoach.app<br />
                  <strong>Address:</strong> PM Coach, Princeton, NJ<br />
                  <strong>Response Time:</strong> We aim to respond within 5 business days
                </p>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">
                This Privacy Policy is designed to be transparent about our practices. If you have questions 
                about any section, please don't hesitate to contact us.
              </p>
            </div>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
};

export default PrivacyPolicy;